import { PlatformLocation, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { filter as _filter, merge as _merge } from 'lodash-es';
import { AppConsts } from '@shared/AppConsts';
import { environment } from './environments/environment';
import { AppSessionService } from '@shared/session/app-session.service';
import { NgxBootstrapDatePickerConfigService } from './ngx-bootstrap/ngx-bootstrap-datepicker-config.service';
import { LanguageService } from '@app/shared/services/language.service';

@Injectable({providedIn: 'root'})
export class AppInitializer {
  private _injector = inject(Injector);
  private _platformLocation = inject(PlatformLocation);
  private _httpClient = inject(HttpClient);
  private _language = inject(LanguageService);

  init(): () => Promise<boolean> {
    return () => {
      return new Promise<boolean>((resolve, reject) => {
        AppConsts.appBaseUrl = environment.appBaseUrl;
        AppConsts.remoteServiceBaseUrl = environment.remoteServiceBaseUrl;
        AppConsts.localeMappings = environment.localeMappings;
        abp.localization.defaultSourceName = AppConsts.localization.defaultLocalizationSourceName;

        this.getUserConfiguration(() => {
          // resolve(true);
          // abp.event.trigger('abp.dynamicScriptsInitialized');
          // do not use constructor injection for AppSessionService
          const appSessionService = this._injector.get(AppSessionService);
          //const cookieConsentService = this._injector.get(CookieConsentService);

          appSessionService.init().then(
            (result) => {
              //cookieConsentService.init();
              if (this.shouldLoadLocale()) {
                const angularLocale = this.convertAbpLocaleToAngularLocale(
                  abp.localization.currentLanguage.name
                );
                import(`../node_modules/@angular/common/locales/${angularLocale}.mjs`).then(
                  (module) => {
                    registerLocaleData(module.default);
                    NgxBootstrapDatePickerConfigService.registerNgxBootstrapDatePickerLocales();
                    resolve(result);
                  },
                  reject
                );
              } else {
                resolve(result);
              }
            },
            (err) => {
              reject(err);
            }
          );
        });
      });
    };
  }

  private getApplicationConfig(appRootUrl: string, callback: () => void) {
    this._httpClient
      .get<any>(`${appRootUrl}${environment.appConfig}`, {
        headers: {
          'Abp.TenantId': `${abp.multiTenancy.getTenantIdCookie()}`,
        },
      })
      .subscribe((response) => {
        AppConsts.appBaseUrl = response.appBaseUrl;
        AppConsts.remoteServiceBaseUrl = response.remoteServiceBaseUrl;
        AppConsts.localeMappings = response.localeMappings;

        callback();
      });
  }

  private getUserConfiguration(callback: () => void): void {
    const cookieLangValue = this._language.languageCookieValue;
    const token = abp.auth.getToken();

    const requestHeaders = {
      'Abp.TenantId': `${abp.multiTenancy.getTenantIdCookie()}`,
      '.AspNetCore.Culture': `c=${cookieLangValue}|uic=${cookieLangValue}`,
    };

    if (token) {
      //requestHeaders['Authorization'] = `Bearer ${token}`;
      Object.assign(requestHeaders, {'Authorization': `Bearer ${token}`});
    }

    this._httpClient.get<any>(`${AppConsts.remoteServiceBaseUrl}/AbpUserConfiguration/GetAll`,
        {
          headers: requestHeaders,
        }
      )
      .subscribe((response) => {
        const result = response.result;

        _merge(abp, result);

        abp.clock.provider = abp.timing.localClockProvider;

        // moment.locale(abp.localization.currentLanguage.name);

        // if (abp.clock.provider.supportsMultipleTimezone) {
        //   moment.tz.setDefault(abp.timing.timeZoneInfo.iana.timeZoneId);
        // }

        callback();
      });
  }

  private getBaseHref(): string {
    const baseUrl = this._platformLocation.getBaseHrefFromDOM();
    if (baseUrl) {
      return baseUrl;
    }

    return '/';
  }

  private getDocumentOrigin(): string {
    if (!document.location.origin) {
      const port = document.location.port ? ':' + document.location.port : '';
      return (
        document.location.protocol + '//' + document.location.hostname + port
      );
    }

    return document.location.origin;
  }

  private shouldLoadLocale(): boolean {
    return (
      !!abp.localization.currentLanguage.name &&
      abp.localization.currentLanguage.name !== 'en-US'
    );
  }

  private convertAbpLocaleToAngularLocale(locale: string): string {
    if (!AppConsts.localeMappings) {
      return locale;
    }

    const localeMapings = _filter(AppConsts.localeMappings, { from: locale });
    if (localeMapings && localeMapings.length) {
      return localeMapings[0]['to'];
    }

    return locale;
  }

}
